/**
 * Offline enrollment (Phase 16A, design §6.3): run online, after a full sign-in with MFA.
 * Storage persistence → device registration → encrypted store with the PIN → first packets.
 */
import type { AuthUser } from '../../types/permissions';
import { TokenService } from '../auth/token.service';
import { DeviceIds, DeviceService } from './deviceService';
import { identityHash } from './crypto';
import { OfflineSession } from './offlineSession';
import { Packets } from './packetService';
import { detectPlatform, deviceLabel } from './platform/deviceClass';
import { freeStorageBytes, MIN_FREE_BYTES, requestPersistentStorage } from './platform/storageReadiness';
import { isOfflineDisabledError, OFFLINE_DISABLED_MESSAGE } from './network';
import { migrateLegacyDrafts } from './draftMigration';
import { Offline } from './offlineServices';

export class EnrollmentError extends Error {
  constructor(message: string, readonly code: 'STORAGE' | 'SPACE' | 'SESSION' | 'PLATFORM' | 'DISABLED') {
    super(message);
    this.name = 'EnrollmentError';
  }
}

export interface EnrollmentResult {
  packets: number | null;
  /** Set when the first packet download failed; enrollment still succeeded. */
  packetError?: string;
  /** Phase 9B drafts moved into the encrypted outbox (16C P3) */
  migratedDrafts?: number;
}

export async function enrollThisDevice(user: AuthUser, pin: string): Promise<EnrollmentResult> {
  const platform = detectPlatform();
  if (platform === 'desktop') throw new EnrollmentError('Offline mode works on phones and tablets only.', 'PLATFORM');

  const persisted = await requestPersistentStorage();
  if (platform === 'android' && !persisted) {
    throw new EnrollmentError('This device would not keep offline data safe. Install pelora from Chrome, open it from the app icon, and try again.', 'STORAGE');
  }
  const free = await freeStorageBytes();
  if (free !== null && free < MIN_FREE_BYTES) {
    throw new EnrollmentError('Free up at least 200 MB of storage on this device, then try again.', 'SPACE');
  }

  const refreshToken = TokenService.getRefreshToken();
  if (!refreshToken) throw new EnrollmentError('Your session has expired. Sign in again, then set up offline mode.', 'SESSION');

  const identity = await identityHash(user.tenantId, user.id);
  let device;
  try {
    device = await DeviceService.register(platform, deviceLabel(), DeviceIds.get(identity));
  } catch (e) {
    // 16D kill switch: registration refused platform-wide
    if (isOfflineDisabledError(e)) throw new EnrollmentError(OFFLINE_DISABLED_MESSAGE, 'DISABLED');
    throw e;
  }
  DeviceIds.set(identity, device.deviceId);
  await OfflineSession.enroll({ user, refreshToken, pin, deviceId: device.deviceId, offlineSettings: device.offlineSettings });

  // 16C P3: unsent Phase 9B drafts move into the encrypted outbox; pelora-drafts is deleted
  const migratedDrafts = await migrateLegacyDrafts(user.id, Offline.local).catch(() => 0);

  try {
    const { patients } = await Packets.refresh();
    return { packets: patients, migratedDrafts };
  } catch (e) {
    return { packets: null, packetError: e instanceof Error ? e.message : 'Could not download your patients yet', migratedDrafts };
  }
}
